import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Card,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";

const statusOptions = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "processing", label: "Processing" },
  { value: "paid", label: "Paid" },
  { value: "rejected", label: "Rejected" },
];

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCurrency = (amount, currency = "INR") =>
  `${currency === "INR" ? "₹" : `${currency} `}${Number(amount || 0).toLocaleString("en-IN")}`;

function WalletWithdrawals() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [updatingId, setUpdatingId] = useState("");
  const [notification, setNotification] = useState(false);
  const [message, setMessage] = useState("");
  const [notiColor, setNotiColor] = useState("success");

  const showNotice = (content, color = "success") => {
    setMessage(content);
    setNotiColor(color);
    setNotification(true);
    setTimeout(() => {
      setNotification(false);
      setMessage("");
      setNotiColor("success");
    }, 2500);
  };

  const loadWithdrawals = () => {
    if (init() !== "success") {
      window.location.assign("/signin");
      return;
    }

    setLoading(true);

    axios
      .get(Constants.getUrls.getWalletWithdrawals, {
        params: {
          page,
          limit,
          search: searchText,
          status: statusFilter,
        },
      })
      .then((resp) => {
        if (resp?.data?.status === "success") {
          setRows(resp?.data?.data?.docs || []);
          setTotalPages(resp?.data?.data?.totalPages || 1);
          setTotalDocs(resp?.data?.data?.totalDocs || 0);
        } else {
          showNotice(resp?.data?.message || "Unable to load withdrawal requests.", "warning");
        }
      })
      .catch((err) => {
        showNotice(err?.response?.data?.message || "Unable to load withdrawal requests.", "error");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadWithdrawals();
  }, [page, limit, searchText, statusFilter]);

  const updateStatus = (row, nextStatus) => {
    if (!row?._id || init() !== "success") {
      return;
    }

    const adminNote = window.prompt(
      `Optional note for marking this request as ${nextStatus}:`,
      row?.admin_note || ""
    );

    if (adminNote === null) {
      return;
    }

    setUpdatingId(row._id);

    axios
      .put(`${Constants.putUrls.updateWalletWithdrawalStatus}${row._id}`, {
        status: nextStatus,
        admin_note: adminNote,
      })
      .then((resp) => {
        showNotice(resp?.data?.message || "Withdrawal request updated successfully.");
        loadWithdrawals();
      })
      .catch((err) => {
        showNotice(err?.response?.data?.message || "Unable to update withdrawal request.", "error");
      })
      .finally(() => {
        setUpdatingId("");
      });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h6" color="white">
              Wallet Withdrawal Requests
            </MDTypography>
          </MDBox>

          <MDBox p={2}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(event) => {
                      setPage(1);
                      setStatusFilter(event.target.value);
                    }}
                  >
                    {statusOptions.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Search by user, bank, IFSC, account or contact"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <MDButton
                  variant="contained"
                  color="info"
                  fullWidth
                  onClick={() => {
                    setPage(1);
                    setSearchText(searchInput.trim());
                  }}
                >
                  Search
                </MDButton>
              </Grid>
            </Grid>
          </MDBox>

          <MDBox py={3}>
            {loading ? (
              <Grid align="center">
                <h4>Loading...</h4>
              </Grid>
            ) : rows.length > 0 ? (
              <>
                <DataTable
                  table={{
                    columns: [
                      { Header: "S.No.", accessor: "serial", width: "6%" },
                      { Header: "Date", accessor: "createdAt" },
                      { Header: "User", accessor: "user" },
                      { Header: "College", accessor: "college" },
                      { Header: "Amount", accessor: "amount" },
                      { Header: "Bank Details", accessor: "bank" },
                      { Header: "Status", accessor: "status" },
                      { Header: "Transaction", accessor: "transaction" },
                      { Header: "Action", accessor: "action" },
                    ],
                    rows: rows.map((row, index) => ({
                      serial: (page - 1) * limit + index + 1,
                      createdAt: formatDate(row?.createdAt),
                      user: (
                        <MDBox>
                          <MDTypography variant="button" fontWeight="bold">
                            {row?.user?.name || "-"}
                          </MDTypography>
                          <MDTypography variant="caption" color="text">
                            {row?.user?.mobile || row?.user?.email || "-"}
                          </MDTypography>
                        </MDBox>
                      ),
                      college: row?.user?.college_short_name || row?.user?.college_name || "-",
                      amount: formatCurrency(row?.amount, row?.currency),
                      bank: (
                        <MDBox>
                          <MDTypography variant="button">{row?.bank_name || "-"}</MDTypography>
                          <MDTypography variant="caption" display="block" color="text">
                            A/C: {row?.account_number_masked || "-"}
                          </MDTypography>
                          <MDTypography variant="caption" display="block" color="text">
                            IFSC: {row?.ifsc_code || "-"}
                          </MDTypography>
                        </MDBox>
                      ),
                      status: (
                        <MDBox>
                          <MDTypography
                            variant="button"
                            color={
                              row?.status === "paid"
                                ? "success"
                                : row?.status === "rejected"
                                ? "error"
                                : row?.status === "processing"
                                ? "info"
                                : "warning"
                            }
                          >
                            {String(row?.status || "pending").toUpperCase()}
                          </MDTypography>
                          {row?.admin_note ? (
                            <MDTypography variant="caption" display="block" color="text">
                              {row.admin_note}
                            </MDTypography>
                          ) : null}
                        </MDBox>
                      ),
                      transaction: row?.transaction_code || "-",
                      action:
                        row?.status === "paid" ? (
                          <MDTypography variant="caption" color="success">
                            Completed
                          </MDTypography>
                        ) : (
                          <MDBox display="flex" gap={1} flexWrap="wrap">
                            {row?.status !== "processing" ? (
                              <MDButton
                                variant="outlined"
                                size="small"
                                color="info"
                                disabled={updatingId === row?._id}
                                onClick={() => updateStatus(row, "processing")}
                              >
                                Processing
                              </MDButton>
                            ) : null}
                            <MDButton
                              variant="outlined"
                              size="small"
                              color="success"
                              disabled={updatingId === row?._id}
                              onClick={() => updateStatus(row, "paid")}
                            >
                              Mark Paid
                            </MDButton>
                            {row?.status !== "rejected" ? (
                              <MDButton
                                variant="outlined"
                                size="small"
                                color="error"
                                disabled={updatingId === row?._id}
                                onClick={() => updateStatus(row, "rejected")}
                              >
                                Reject
                              </MDButton>
                            ) : null}
                          </MDBox>
                        ),
                    })),
                  }}
                  isSorted={false}
                  entriesPerPage={{ defaultValue: limit, entries: [10, 25, 50, 100] }}
                  showTotalEntries
                  page_no={page}
                  totalPages={totalPages}
                  totalDocs={totalDocs}
                  onPageChange={setPage}
                  onEntriesPerPageChange={(nextLimit) => {
                    setLimit(nextLimit);
                    setPage(1);
                  }}
                  noEndBorder
                />
              </>
            ) : (
              <MDBox px={3}>
                <MDTypography variant="button" color="text">
                  No withdrawal requests found.
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        </Card>
      </MDBox>

      <MDSnackbar
        color={notiColor}
        icon="notifications"
        title="AgriQuiz"
        content={message}
        dateTime=""
        open={notification}
        onClose={() => setNotification(false)}
        close={() => setNotification(false)}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default WalletWithdrawals;
